import { useLegalEntities } from '../../../hooks/useLegalEntities';
import { useMovementTypes } from '../../../hooks/useMovementTypes';
import { useEffect, useMemo, useState } from 'react';
import { Box, Table, TableBody, TableHead, TableRow, Typography, MenuItem } from '@mui/material';
import { GetApp as ExportIcon, Search as SearchIcon } from '@mui/icons-material';

import { useBankMovementsByDateRange } from '../../../hooks/useBankMovements';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  LoadingSkeleton,
  CustomButton,
  CustomTextField,
  CustomAutoComplete,
  HorizontalForm,
  PaginationTable,
} from '../../../common/components';
import MovementRow from './MovementRow';
import { DEFAULT_PAGINATION } from '../constants/bankMovementsConstants';
import { formatCurrency, formatDate } from '../utils/formatters';
import useBankMovementsFiltersStore from '../../../stores/useBankMovementsFiltersStore';

const MovementsList = ({ account, onEditMovement }) => {
  const { data: legalEntities = [] } = useLegalEntities();
  const { data: movementTypes = [] } = useMovementTypes();
  const defaultFromDate = useMemo(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    []
  );
  const defaultToDate = useMemo(() => new Date().toISOString().split('T')[0], []);

  const { getDateRange, setDateRange, hydrated } = useBankMovementsFiltersStore();

  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);
  const [searchTerm, setSearchTerm] = useState('');
  const [reconciliationFilter, setReconciliationFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGINATION.rowsPerPage);

  const {
    data: movements = [],
    isLoading,
    refetch,
  } = useBankMovementsByDateRange({
    start_date: fromDate,
    end_date: toDate,
    account_id: account.uuid,
  });

  const filteredMovements = movements.filter((movement) => {
    const matchesSearch = (() => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        movement.concept?.toLowerCase().includes(search) ||
        movement.voucher_number?.toLowerCase().includes(search) ||
        movement.legal_entity_name?.toLowerCase().includes(search) ||
        movement.amount?.toString().includes(searchTerm)
      );
    })();

    const matchesReconciliation =
      reconciliationFilter === 'all' ||
      (reconciliationFilter === 'reconciled' && movement.is_reconciled) ||
      (reconciliationFilter === 'pending' && !movement.is_reconciled);

    const matchesType = typeFilter === 'all' || movement.uuid_movement_type === typeFilter;

    return matchesSearch && matchesReconciliation && matchesType;
  });

  const totalCount = filteredMovements.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage) || 1);
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * rowsPerPage;
  const paginatedMovements = filteredMovements.slice(startIndex, startIndex + rowsPerPage);

  const movementsWithDailyClosing = useMemo(() => {
    if (paginatedMovements.length === 0) return [];

    const getDateKey = (value) => {
      if (!value) return null;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        return trimmed.includes('T') ? trimmed.split('T')[0] : trimmed;
      }
      const dateObj = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(dateObj.getTime())) return null;
      return dateObj.toISOString().split('T')[0];
    };

    const today = new Date().toISOString().split('T')[0];
    const rows = [];
    let lastMovementUpToToday = null;

    filteredMovements.forEach((movement) => {
      const movementDateKey = getDateKey(movement.due_date) || getDateKey(movement.emission_date);
      if (movementDateKey && movementDateKey <= today) {
        lastMovementUpToToday = movement;
      }
    });

    paginatedMovements.forEach((movement) => {
      const currentDateKey = getDateKey(movement.due_date) || getDateKey(movement.emission_date);
      rows.push({ type: 'movement', movement, key: movement.uuid, dateKey: currentDateKey });

      if (lastMovementUpToToday && movement.uuid === lastMovementUpToToday.uuid) {
        rows.push({
          type: 'closing',
          key: `closing-${today}-${movement.uuid}`,
          date: today,
          bookBalance: movement.accumulated_balance,
          realBalance: movement.accumulated_real_balance,
        });
      }
    });

    return rows;
  }, [paginatedMovements, filteredMovements]);

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  const resetDateRange = () => {
    setFromDate(defaultFromDate);
    setToDate(defaultToDate);
    if (hydrated && account?.uuid) {
      setDateRange(account.uuid, defaultFromDate, defaultToDate);
    }
  };

  const isRangeTooWide = (from, to) => {
    if (!from || !to) return false;
    const fromTime = new Date(from).getTime();
    const toTime = new Date(to).getTime();
    if (Number.isNaN(fromTime) || Number.isNaN(toTime)) return false;
    const diffMs = Math.abs(toTime - fromTime);
    const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
    return diffMs > ONE_YEAR_MS;
  };

  useEffect(() => {
    if (!hydrated || !account?.uuid) return;
    const stored = getDateRange(account.uuid);
    if (stored?.fromDate || stored?.toDate) {
      const candidateFrom = stored.fromDate || defaultFromDate;
      const candidateTo = stored.toDate || defaultToDate;
      if (isRangeTooWide(candidateFrom, candidateTo)) {
        resetDateRange();
      } else {
        setFromDate(candidateFrom);
        setToDate(candidateTo);
      }
    } else {
      setDateRange(account.uuid, defaultFromDate, defaultToDate);
      setFromDate(defaultFromDate);
      setToDate(defaultToDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.uuid, getDateRange, setDateRange, defaultFromDate, defaultToDate, hydrated]);

  import('../utils/exportExcel').then(({ exportMovementsToExcel }) => {
    window.exportMovementsToExcel = exportMovementsToExcel;
  });

  const handleExportExcel = async () => {
    if (window.exportMovementsToExcel) {
      await window.exportMovementsToExcel(
        filteredMovements,
        `Movimientos_${account?.name || 'cuenta'}.xlsx`
      );
    }
  };

  const handleFromDateChange = (event) => {
    const value = event.target.value;
    const newFromDate = value;
    const newToDate = toDate;

    if (isRangeTooWide(newFromDate, newToDate)) {
      resetDateRange();
    } else {
      setFromDate(newFromDate);
      if (account?.uuid) {
        setDateRange(account.uuid, newFromDate, newToDate);
      }
    }
    setPage(1);
  };

  const handleToDateChange = (event) => {
    const value = event.target.value;
    const newFromDate = fromDate;
    const newToDate = value;

    if (isRangeTooWide(newFromDate, newToDate)) {
      resetDateRange();
    } else {
      setToDate(newToDate);
      if (account?.uuid) {
        setDateRange(account.uuid, newFromDate, newToDate);
      }
    }
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleReconciliationChange = (event) => {
    setReconciliationFilter(event.target.value);
    setPage(1);
  };

  const handleTypeChange = (event) => {
    setTypeFilter(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
      }}
    >
      <HorizontalForm>
        <CustomTextField
          label="Desde"
          type="date"
          value={fromDate}
          onChange={handleFromDateChange}
          size="medium"
          sx={{ maxWidth: 180, mr: 2 }}
          shrinkLabel
        />

        <CustomTextField
          label="Hasta"
          type="date"
          value={toDate}
          onChange={handleToDateChange}
          size="medium"
          sx={{ maxWidth: 180, mr: 2 }}
          shrinkLabel
        />

        <CustomTextField
          placeholder="Buscar por concepto, comprobante o beneficiario"
          value={searchTerm}
          onChange={handleSearchChange}
          size="medium"
          sx={{ maxWidth: 430, mr: 2 }}
        />

        <CustomAutoComplete
          label="Conciliado"
          value={reconciliationFilter}
          onChange={handleReconciliationChange}
          size="medium"
          sx={{ maxWidth: 150, mr: 2 }}
        >
          <MenuItem value="all">Todos</MenuItem>
          <MenuItem value="reconciled">Si</MenuItem>
          <MenuItem value="pending">No</MenuItem>
        </CustomAutoComplete>

        <CustomAutoComplete
          label="Tipo"
          value={typeFilter}
          onChange={handleTypeChange}
          size="medium"
          sx={{ maxWidth: 200, mr: 2 }}
        >
          <MenuItem value="all">Todos</MenuItem>
          {movementTypes
            .filter((type) => type.is_active !== false)
            .map((type) => (
              <MenuItem key={type.uuid} value={type.uuid}>
                {type.description}
              </MenuItem>
            ))}
        </CustomAutoComplete>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            ml: { md: 'auto' },
            mt: { xs: 2, md: 0 },
            alignSelf: { md: 'flex-end' },
            alignItems: 'center',
          }}
        >
          <CustomButton
            onClick={refetch}
            size="medium"
            buttonIcon={SearchIcon}
            sx={{ minHeight: 60 }}
          >
            Buscar
          </CustomButton>

          <CustomButton
            variant="info"
            buttonIcon={ExportIcon}
            onClick={handleExportExcel}
            size="small"
            sx={{ minHeight: 60 }}
          >
            Exportar XLSX
          </CustomButton>
        </Box>
      </HorizontalForm>

      <GeneralTableBox>
        <Table size="small">
          <TableHead>
            <TableRow>
              <HeadTableCell>Fecha emi.asdasd</HeadTableCell>
              <HeadTableCell>Fecha venc.</HeadTableCell>
              <HeadTableCell>Tipo</HeadTableCell>
              <HeadTableCell>Concepto</HeadTableCell>
              <HeadTableCell>N° comp.</HeadTableCell>
              <HeadTableCell>Beneficiario</HeadTableCell>
              <HeadTableCell>Importe</HeadTableCell>
              <HeadTableCell>Saldo Libro</HeadTableCell>
              <HeadTableCell>Acciones</HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {movementsWithDailyClosing.map((row) =>
              row.type === 'movement' ? (
                <MovementRow
                  key={row.key}
                  movement={row.movement}
                  movementTypes={movementTypes}
                  legalEntities={legalEntities}
                  onEdit={onEditMovement}
                />
              ) : (
                (() => {
                  const formattedDate = row.date ? formatDate(row.date) : 'Sin fecha';
                  return (
                    <TableRow
                      key={row.key}
                      sx={{
                        backgroundColor: 'grey.200',
                      }}
                    >
                      <RowTableCell colSpan={9}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                            gap: 2,
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                          }}
                        >
                          <Typography fontWeight="bold" sx={{ ml: 10, py: 2 }}>
                            Saldo real al {formattedDate}:{' '}
                            <Box component="span" sx={{ fontFamily: 'monospace' }}>
                              {formatCurrency(row.realBalance, 2)}
                            </Box>
                          </Typography>
                          <Typography fontWeight="bold" sx={{ mr: 10, py: 2 }}>
                            Saldo libro al {formattedDate}:{' '}
                            <Box component="span" sx={{ fontFamily: 'monospace' }}>
                              {formatCurrency(row.bookBalance, 2)}
                            </Box>
                          </Typography>
                        </Box>
                      </RowTableCell>
                    </TableRow>
                  );
                })()
              )
            )}
            {filteredMovements.length === 0 && (
              <TableRow>
                <RowTableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No se encontraron movimientos
                  </Typography>
                </RowTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>

      {totalCount > rowsPerPage && (
        <PaginationTable
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(1);
          }}
          sx={{ mt: 2 }}
        />
      )}
    </Box>
  );
};

export default MovementsList;
